import { ComputerDesktopIcon, DevicePhoneMobileIcon } from "@heroicons/react/24/outline";
import { router } from "@inertiajs/react";
import { useEffect, useState } from "react";
import { viewportLabel } from "@/components/audits/replay/helpers";
import ReportFalsePositiveDialog from "@/components/domains/ReportFalsePositiveDialog";
import CopyCodingAgentPrompt from "@/components/issues/CopyCodingAgentPrompt";
import IssueDetailModal from "@/components/issues/IssueDetailModal";
import type { DomainIssue, PageIssuePriority } from "@/types";
import { formatRelativeTime } from "@/utils/formatters";

type Props = {
  domainId: string;
  issue: DomainIssue;
  staff?: boolean;
  forceExpanded?: boolean;
  onWatchIssue: (issue: DomainIssue) => void;
};

export default function OpenIssueRow({ domainId, issue, staff = false, forceExpanded = false, onWatchIssue }: Props) {
  const canWatch = Boolean(issue.player_focus);
  const reported = issue.reported_false_positive;
  const [open, setOpen] = useState(forceExpanded && !canWatch);
  const [originEl, setOriginEl] = useState<HTMLElement | null>(null);
  const [reporting, setReporting] = useState(false);

  useEffect(() => {
    if (forceExpanded && !canWatch) {
      setOpen(true);
    }
  }, [forceExpanded, canWatch]);

  const locationLabel = issue.location_label;
  const lastSeenLabel = issue.last_seen_at ? formatRelativeTime(issue.last_seen_at) : null;
  const viewportSummary =
    issue.viewports.length > 0 ? issue.viewports.map((viewport) => viewportLabel(viewport)).join(" + ") : null;
  const titleId = `issue-title-${issue.id}`;

  function removeFalsePositiveReport() {
    router.delete(`/domains/${domainId}/issues/${issue.id}/false_positive_report`, { preserveScroll: true });
  }

  return (
    <article id={`issue-${issue.id}`} className="scroll-mt-5 rounded-lg border border-gray-200 bg-white">
      <div className="relative flex flex-col items-stretch gap-3 px-4 py-4 transition hover:bg-gray-50 md:flex-row md:items-start">
        <button
          type="button"
          className="absolute inset-0 cursor-pointer rounded-lg"
          onClick={(event) => {
            if (canWatch) {
              onWatchIssue(issue);
              return;
            }

            setOriginEl(event.currentTarget);
            setOpen(true);
          }}
          aria-expanded={canWatch ? undefined : open}
          aria-haspopup={canWatch ? undefined : "dialog"}
          aria-labelledby={titleId}
        />
        <div className={`pointer-events-none relative min-w-0 flex-1 ${reported ? "opacity-40" : ""}`}>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <PriorityBadge priority={issue.priority} />
            {issue.heuristic?.name ? (
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{issue.heuristic.name}</p>
            ) : null}
            {reported ? (
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Hidden as false positive</p>
            ) : null}
          </div>
          <h3 id={titleId} className="font-semibold text-gray-900">
            {issue.title || "Untitled issue"}
          </h3>

          <p className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-gray-500">
            {viewportSummary ? (
              <span className="inline-flex items-center gap-1">
                <span className="inline-flex items-center gap-0.5" aria-hidden="true">
                  {issue.viewports.map((viewport) => {
                    const Icon = viewport === "mobile" ? DevicePhoneMobileIcon : ComputerDesktopIcon;
                    return <Icon key={viewport} className="h-3.5 w-3.5 text-gray-400" />;
                  })}
                </span>
                {viewportSummary}
              </span>
            ) : null}
            {viewportSummary && (locationLabel || lastSeenLabel) ? <span aria-hidden="true">·</span> : null}
            {locationLabel ? <span>{locationLabel}</span> : null}
            {locationLabel && lastSeenLabel ? <span aria-hidden="true">·</span> : null}
            {lastSeenLabel ? <span>Seen {lastSeenLabel}</span> : null}
          </p>
        </div>
        <div className="relative z-10 flex shrink-0 flex-col items-start gap-2 md:items-end">
          <CopyCodingAgentPrompt key={issue.id} issue={issue} variant="row" />
          {staff ? (
            reported ? (
              <button
                type="button"
                className="cursor-pointer text-sm font-medium text-gray-600 hover:underline"
                onClick={removeFalsePositiveReport}
              >
                Show again
              </button>
            ) : (
              <button
                type="button"
                className="cursor-pointer text-sm font-medium text-gray-600 hover:underline"
                onClick={() => setReporting(true)}
              >
                Report false positive
              </button>
            )
          ) : null}
        </div>
      </div>

      {open ? (
        <IssueDetailModal
          issue={issue}
          group={issue.priority}
          originEl={originEl}
          portal
          onClose={() => setOpen(false)}
        />
      ) : null}

      {staff ? (
        <ReportFalsePositiveDialog
          domainId={domainId}
          issueId={issue.id}
          open={reporting}
          onClose={() => setReporting(false)}
        />
      ) : null}
    </article>
  );
}

function PriorityBadge({ priority }: { priority: PageIssuePriority }) {
  const styles = {
    high: "bg-error-light text-error-dark",
    medium: "bg-warning-light text-warning-dark",
    low: "bg-gray-100 text-gray-600",
  }[priority];

  return (
    <span className={`rounded-md px-2 py-1 text-xs font-semibold uppercase tracking-wide ${styles}`}>{priority}</span>
  );
}
